import type { HTMLAttributes, ReactNode } from 'react'
import { cn } from './cn'

type DeltaTone = 'up' | 'down' | 'flat'
const deltaTones: Record<DeltaTone, string> = { up: 'text-ok-ink', down: 'text-err-ink', flat: 'text-ink-muted' }

export interface KpiProps extends HTMLAttributes<HTMLDivElement> {
  label?: ReactNode
  value?: ReactNode
  unit?: ReactNode
  delta?: ReactNode
  deltaTone?: DeltaTone
  meta?: ReactNode
  /** @deprecated Use `onInk`. */
  ink?: boolean
  /** Canonical on-ink spelling; maps to the legacy `ink` prop. */
  onInk?: boolean
  children?: ReactNode
}

/** Kpi — a metric tile (drop inside a Module or Card). */
export function Kpi({
  label,
  value,
  unit,
  delta,
  deltaTone = 'flat',
  meta,
  ink = false,
  onInk,
  className = '',
  children,
  ...props
}: KpiProps) {
  const resolvedInk = onInk ?? ink

  return (
    <div className={cn('flex flex-col gap-2 p-5', className)} {...props}>
      <span
        className={cn(
          'flex items-center gap-2 text-xs font-semibold uppercase tracking-eyebrow',
          resolvedInk ? 'text-on-ink-tertiary' : 'text-ink-muted',
        )}
      >
        {label}
      </span>
      <span className={cn('font-display font-normal text-kpi', resolvedInk ? 'text-on-ink' : 'text-strong')}>
        {value}
        {unit && <span className="text-mustard">{unit}</span>}
      </span>
      {(delta || meta) && (
        <span className={cn('flex items-center gap-3 text-sm', resolvedInk ? 'text-on-ink-soft' : 'text-ink-muted')}>
          {delta && (
            <span className={cn('inline-flex items-center gap-1 font-semibold', deltaTones[deltaTone])}>{delta}</span>
          )}
          {meta}
        </span>
      )}
      {children}
    </div>
  )
}
